<script setup>
// NOTICE: KAPTION-INTEGRATION — Floating popover that previews a WhatsApp template
// using the shared iframe renderer (KaptionWhatsAppPreviewEnhanced). A SINGLE warm
// iframe instance stays mounted and is retargeted via the `template` prop
// (postMessage UPDATE_TEMPLATE, no reload) so previews appear instantly as the user
// hovers different campaign rows. Positioned (teleported, fixed) next to its anchor.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KaptionWhatsAppPreviewEnhanced from './KaptionWhatsAppPreviewEnhanced.vue';

const props = defineProps({
  visible: { type: Boolean, default: false },
  // Title shown in the popover header (the campaign name).
  title: { type: String, default: '' },
  // The WhatsApp template object: { category, language?, status?, components: [...] }.
  template: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  // True when the template could not be resolved (deleted / not synced).
  error: { type: Boolean, default: false },
  // Anchor rect (getBoundingClientRect of the trigger) used to position the popover.
  anchor: { type: Object, default: null },
  // When pinned (opened by click) we show a close button and keep it open on leave.
  pinned: { type: Boolean, default: false },
});

const emit = defineEmits(['mouseenter', 'mouseleave', 'close']);
const { t } = useI18n();

const POPOVER_W = 320;
const POPOVER_H = 470;

// Position the popover to the right of the anchor, flipping left / clamping
// vertically so it always stays inside the viewport.
const style = computed(() => {
  const a = props.anchor;
  if (!a) return { display: 'none' };
  const gap = 8;
  let left = a.right + gap;
  if (left + POPOVER_W > window.innerWidth - 8) {
    left = Math.max(8, a.left - POPOVER_W - gap);
  }
  let top = a.top - 8;
  if (top + POPOVER_H > window.innerHeight - 8) {
    top = Math.max(8, window.innerHeight - POPOVER_H - 8);
  }
  top = Math.max(8, top);
  return { top: `${top}px`, left: `${left}px`, width: `${POPOVER_W}px` };
});

const meta = computed(() => {
  const tpl = props.template;
  if (!tpl) return '';
  return [tpl.category, tpl.language, tpl.status].filter(Boolean).join(' · ');
});
</script>

<template>
  <teleport to="body">
    <!-- v-show (not v-if) keeps the inner iframe mounted & warm between opens. -->
    <div
      v-show="visible"
      class="fixed z-[9999] flex flex-col rounded-xl border border-n-weak bg-n-alpha-3 backdrop-blur-[50px] outline outline-1 outline-n-weak shadow-lg overflow-hidden"
      :style="style"
      data-testid="template-preview-popover"
      @mouseenter="emit('mouseenter')"
      @mouseleave="emit('mouseleave')"
    >
      <div
        class="flex items-center gap-2 px-3 py-2 border-b border-n-weak bg-n-alpha-black2"
      >
        <span
          class="i-lucide-message-square-text w-3.5 h-3.5 text-n-slate-10"
        />
        <span class="text-xs font-semibold text-n-slate-12">
          {{ t('CAMPAIGN.REPORT.PREVIEW.TITLE') }}
        </span>
        <span v-if="title" class="text-xs text-n-slate-10 truncate ml-1 flex-1">
          {{ title }}
        </span>
        <button
          v-if="pinned"
          type="button"
          class="i-lucide-x w-3.5 h-3.5 text-n-slate-10 hover:text-n-slate-12 shrink-0"
          :aria-label="t('CAMPAIGN.REPORT.PREVIEW.CLOSE')"
          @click="emit('close')"
        />
      </div>

      <div class="relative h-[380px]">
        <!-- The real, pixel-perfect renderer. Kept mounted; retargeted via :template. -->
        <KaptionWhatsAppPreviewEnhanced
          class="absolute inset-0 w-full h-full"
          :template="error ? null : template"
          :loading="loading"
          mode="render"
          bare
          truncate
        />
        <div
          v-if="error"
          class="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-n-solid-1 text-center px-4"
        >
          <span class="i-lucide-image-off w-7 h-7 text-n-slate-8" />
          <span class="text-xs text-n-slate-10">
            {{ t('CAMPAIGN.REPORT.PREVIEW.UNAVAILABLE') }}
          </span>
        </div>
      </div>

      <div
        v-if="meta && !error"
        class="px-3 py-2 border-t border-n-weak text-[11px] text-n-slate-10 uppercase tracking-wide"
      >
        {{ meta }}
      </div>
    </div>
  </teleport>
</template>
